import React, { useState, useEffect } from 'react'
import { useLanguage } from '../i18n'
import API_CONFIG from '../config/api'
import { getMockTeamData, getMockWeeklyRankings } from '../services/mockData'

// 团队信息组件
const TeamInfo = ({ teamInfo, t }) => {
  if (!teamInfo) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner"></div>
          <span className="ml-3 text-gray-300">{t('teamDashboard.loadingTeamData')}</span>
        </div>
      </div>
    )
  }

  const { team, memberInfo, teamMembers, teamStats } = teamInfo

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-6 text-center">{t('teamDashboard.myFleet')}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="text-center p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-blue-400">{teamStats.totalMembers}</div>
          <div className="text-sm text-gray-300">{t('teamDashboard.fleetMembers')}</div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-green-400">{team.team_name}</div>
          <div className="text-sm text-gray-300">{t('teamDashboard.fleetName')}</div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl">
          <div className="text-2xl font-bold text-purple-400 font-mono">{team.invite_code}</div>
          <div className="text-sm text-gray-300">{t('teamDashboard.inviteCode')}</div>
        </div>
      </div>

      {/* 成员列表 */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3">{t('teamDashboard.fleetMembers')}</h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {teamMembers.map((member, index) => (
            <div key={index} className={`flex items-center justify-between p-2 rounded-lg ${
              member.role === 'captain' 
                ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' 
                : 'bg-gray-800/30'
            }`}>
              <div className="flex items-center">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-2 ${
                  member.role === 'captain' ? 'bg-yellow-500 text-black' : 'bg-gray-600 text-gray-300'
                }`}>
                  {member.role === 'captain' ? '👑' : '👤'}
                </span>
                <span className="font-mono text-sm">
                  {member.member_address.slice(0, 8)}...{member.member_address.slice(-6)}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {new Date(member.joined_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 邀请码分享 */}
      <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl p-4 border border-green-500/20">
        <h4 className="font-semibold mb-2">{t('teamDashboard.inviteFriends')}</h4>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={team.invite_code}
            readOnly
            className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-600 rounded-lg text-white font-mono text-sm"
          />
          <button
            onClick={() => navigator.clipboard.writeText(team.invite_code)}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg font-semibold text-sm hover:from-green-600 hover:to-blue-600 transition-colors"
          >
            {t('teamDashboard.copy')}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {t('teamDashboard.shareInviteCode')}
        </p>
      </div>
    </div>
  )
}

// 加入团队组件
const JoinTeamPanel = ({ onJoinTeam, t }) => {
  const [inviteCode, setInviteCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      alert(t('teamDashboard.enterInviteCode'))
      return
    }

    setIsLoading(true)
    try {
      await onJoinTeam(inviteCode.trim().toUpperCase())
      setInviteCode('')
    } catch (error) {
      console.error('加入团队失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('teamDashboard.joinFleet')}</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">{t('teamDashboard.inviteCode')}</label>
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder={t('teamDashboard.inviteCodePlaceholder')}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
        
        <button
          onClick={handleJoin}
          disabled={isLoading}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('teamDashboard.joining') : t('teamDashboard.joinFleetBtn')}
        </button>
        
        <div className="text-center text-gray-400 text-sm">
          <p>{t('teamDashboard.joinFleetDesc')}</p>
        </div>
      </div>
    </div>
  )
}

// 周榜排行榜组件
const WeeklyRankings = ({ rankings, t }) => {
  if (!rankings || rankings.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h3 className="text-2xl font-bold mb-4 text-center">{t('teamDashboard.thisWeekRankings')}</h3>
        <p className="text-gray-400 text-center py-8">{t('teamDashboard.noRankingData')}</p>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-6 text-center">{t('teamDashboard.thisWeekRankingsTop20')}</h3>
      
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {rankings.map((team, index) => (
          <div 
            key={team.teamId}
            className={`flex items-center justify-between p-4 rounded-2xl transition-all duration-300 hover:scale-105 ${
              index < 3 
                ? 'bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 border-2 border-yellow-500/30 shadow-lg' 
                : 'bg-gray-800/30 border border-gray-600/30'
            }`}
          >
            <div className="flex items-center space-x-4">
              <span className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                index === 0 ? 'bg-yellow-500 text-black shadow-lg' :
                index === 1 ? 'bg-gray-400 text-black shadow-md' :
                index === 2 ? 'bg-orange-500 text-black shadow-sm' :
                'bg-gray-600 text-gray-300'
              }`}>
                {index + 1}
              </span>
              
              <div>
                <div className="font-semibold text-lg">{team.teamName}</div>
                <div className="text-sm text-gray-400 font-mono">
                  {team.captainAddress.slice(0, 6)}...{team.captainAddress.slice(-4)}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-2xl font-bold text-green-400">{team.totalContribution}</div>
              <div className="text-sm text-gray-400">
                {team.teamSize} {t('teamDashboard.members')}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center text-gray-400 text-sm">
        <p>{t('teamDashboard.contributionRule1')}</p>
        <p>{t('teamDashboard.contributionRule2')}</p>
      </div>
    </div>
  )
}

// 贡献度计算说明组件
const ContributionExplanation = ({ t }) => {
  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-4 text-center">{t('teamDashboard.contributionExplanation')}</h3>
      
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl">
          <h4 className="font-semibold mb-2">{t('teamDashboard.captainContribution')}</h4>
          <p className="text-sm text-gray-300">
            {t('teamDashboard.captainContributionFormula')}
          </p>
        </div>
        
        <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl">
          <h4 className="font-semibold mb-2">{t('teamDashboard.memberContribution')}</h4>
          <p className="text-sm text-gray-300">
            {t('teamDashboard.memberContributionFormula')}
          </p>
        </div>
        
        <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl">
          <h4 className="font-semibold mb-2">{t('teamDashboard.teamTotalContribution')}</h4>
          <p className="text-sm text-gray-300">
            {t('teamDashboard.teamTotalContributionFormula')}
          </p>
        </div>
        
        <div className="text-center text-gray-400 text-sm">
          <p>{t('teamDashboard.tip')}</p>
        </div>
      </div>
    </div>
  )
}

// 主团队仪表盘组件
const TeamDashboard = ({ isConnected, walletAddress }) => {
  const { t } = useLanguage()
  const [teamInfo, setTeamInfo] = useState(null)
  const [rankings, setRankings] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasTeam, setHasTeam] = useState(false)

  useEffect(() => {
    loadTeamData()
    
    // 设置定时刷新
    const interval = setInterval(() => {
      loadTeamData(false)
    }, 30000) // 30秒刷新一次

    return () => clearInterval(interval)
  }, [isConnected, walletAddress])

  const loadTeamData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true)
      
      // 检查是否使用模拟数据
      if (!API_CONFIG.hasBackend()) {
        setRankings(getMockWeeklyRankings())
        if (isConnected && walletAddress) {
          setTeamInfo(getMockTeamData())
          setHasTeam(true)
        }
        if (showLoading) setIsLoading(false)
        return
      }
      
      // 获取周榜排名
      const rankingsResponse = await fetch(`${API_CONFIG.baseURL}/teams/weekly-rankings`)
      const rankingsData = await rankingsResponse.json()
      
      if (rankingsData.success) {
        setRankings(rankingsData.data.rankings)
      }

      // 如果已连接钱包，获取用户团队信息
      if (isConnected && walletAddress) {
        try {
          const teamResponse = await fetch(`${API_CONFIG.baseURL}/teams/user/${walletAddress}`)
          const teamData = await teamResponse.json()
          
          if (teamData.success && teamData.data) {
            setTeamInfo(teamData.data)
            setHasTeam(true)
          } else {
            setHasTeam(false)
          }
        } catch (error) {
          console.error('获取团队信息失败:', error)
          setHasTeam(false)
        }
      } else {
        setHasTeam(false)
      }

    } catch (error) {
      console.error('加载团队数据失败:', error)
      // 出错时使用模拟数据
      setRankings(getMockWeeklyRankings())
      if (isConnected && walletAddress) {
        setTeamInfo(getMockTeamData())
        setHasTeam(true)
      }
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  const handleJoinTeam = async (inviteCode) => {
    if (!isConnected || !walletAddress) {
      alert(t('teamDashboard.pleaseConnectWallet'))
      return
    }

    try {
      const response = await fetch('http://localhost:3001/api/teams/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walletAddress,
          inviteCode
        })
      })

      const data = await response.json()
      
      if (data.success) {
        alert(t('teamDashboard.joinSuccess'))
        await loadTeamData(false)
      } else {
        alert(data.error || t('teamDashboard.joinFailed'))
      }
    } catch (error) {
      console.error('加入团队失败:', error)
      alert(t('teamDashboard.joinFailed') + t('teamDashboard.pleaseRetry'))
    }
  }

  const handleCreateTeam = async () => {
    if (!isConnected || !walletAddress) {
      alert(t('teamDashboard.pleaseConnectWallet'))
      return
    }

    try {
      const response = await fetch('http://localhost:3001/api/teams/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walletAddress
        })
      })

      const data = await response.json()
      
      if (data.success) {
        alert(t('teamDashboard.createSuccess'))
        await loadTeamData(false)
      } else {
        alert(data.error || t('teamDashboard.createFailed'))
      }
    } catch (error) {
      console.error('创建团队失败:', error)
      alert(t('teamDashboard.createFailed') + t('teamDashboard.pleaseRetry'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-300">{t('teamDashboard.loadingTeamData')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 周榜排名 */}
      <WeeklyRankings rankings={rankings} t={t} />

      {hasTeam ? (
        /* 已有团队 */
        <TeamInfo teamInfo={teamInfo} t={t} />
      ) : (
        /* 未加入团队 */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <JoinTeamPanel onJoinTeam={handleJoinTeam} t={t} />
          
          <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
            <h3 className="text-2xl font-bold mb-4 text-center">{t('teamDashboard.createFleet')}</h3>
            <div className="text-center py-8">
              <p className="text-gray-300 mb-4">{t('teamDashboard.noFleetYet')}</p>
              <button
                onClick={handleCreateTeam}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                {t('teamDashboard.createMyFleet')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 贡献度说明 */}
      <ContributionExplanation t={t} />
    </div>
  )
}

export default TeamDashboard
